import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { HttpImgSrcPipe } from '@app/pipes/http-img-src.pipe';
import { Medium } from '@app/shared/components/formly-components/formly-field-picture/formly-field-picture.component';
import { DynamicIconComponent } from '@app/shared/components/dynamic-icon';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-carousel-modal',
  templateUrl: './carousel-modal.component.html',
  styleUrls: ['./carousel-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    IonicModule,
    HttpImgSrcPipe,
    DynamicIconComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CarouselModalComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() media: Medium[] = [];
  @Input() initialSlide: number = 0;
  @ViewChild('swiper', { static: false }) swiperRef?: ElementRef;
  private modalEl?: HTMLIonModalElement;
  private isResizing = false;
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;
  private hasMoved = false;
  private readonly storageKey = 'carouselModalSize';
  private readonly minWidth = 320;
  private readonly minHeight = 240;
  private readonly maxWidthRatio = 0.98;
  private readonly maxHeightRatio = 0.9;
  private boundMove?: (event: MouseEvent) => void;
  private boundUp?: (event: MouseEvent) => void;
  private desiredWidth = 0;
  private desiredHeight = 0;
  
  constructor(
    private modalController: ModalController,
    private hostRef: ElementRef<HTMLElement>,
    private httpClient: HttpClient,
  ) {}

  ngOnInit(): void {
    const saved = this.loadSize();
    const defaultWidth = Math.round(window.innerWidth * 0.9);
    const defaultHeight = Math.round(window.innerHeight * 0.8);
    this.resolveModalEl();
    this.applySize(saved?.width ?? defaultWidth, saved?.height ?? defaultHeight);
  }

  ngAfterViewInit(): void {
    const swiperEl = this.swiperRef?.nativeElement;
    if (swiperEl) {
      const swiperParams = {
        initialSlide: this.initialSlide,
        zoom: {
          maxRatio: 3,
        },
        navigation: true,
        pagination: {
          clickable: true,
        },
        keyboard: {
          enabled: true,
        },
        loop: false,
      };

      Object.assign(swiperEl, swiperParams);
      swiperEl.initialize();
    }
  }

  dismiss(): void {
    console.log('Dismissing carousel modal');
    this.modalController.dismiss();
  }

  async saveCurrentImage(): Promise<void> {
    const active = this.getActiveMedia();
    if (!active) {
      return;
    }
    const url = this.getMediaDownloadUrl(active.fileId);
    if (!url) {
      return;
    }

    try {
      const blob = await firstValueFrom(this.httpClient.get(url, { responseType: 'blob' }));
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = active.originalname || 'image';
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Ignore download errors
    }
  }

  ngOnDestroy(): void {
    this.detachResizeListeners();
  }

  public startResize(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    this.hasMoved = false;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startWidth = this.getCurrentWidth();
    this.startHeight = this.getCurrentHeight();
    this.resolveModalEl();
    const rect = this.modalEl?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      this.startWidth = rect.width;
      this.startHeight = rect.height;
    }
    this.attachResizeListeners();
  }

  private attachResizeListeners(): void {
    this.boundMove = (evt: MouseEvent) => this.onResizeMove(evt);
    this.boundUp = () => this.stopResize();
    document.addEventListener('mousemove', this.boundMove);
    document.addEventListener('mouseup', this.boundUp);
  }

  private detachResizeListeners(): void {
    if (this.boundMove) {
      document.removeEventListener('mousemove', this.boundMove);
    }
    if (this.boundUp) {
      document.removeEventListener('mouseup', this.boundUp);
    }
    this.boundMove = undefined;
    this.boundUp = undefined;
  }

  private onResizeMove(event: MouseEvent): void {
    if (!this.isResizing) {
      return;
    }
    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;
    if (Math.abs(deltaX) + Math.abs(deltaY) < 2) {
      return;
    }
    this.hasMoved = true;
    const targetWidth = this.startWidth + deltaX;
    const targetHeight = this.startHeight + deltaY;
    this.applySize(targetWidth, targetHeight);
  }

  private stopResize(): void {
    if (!this.isResizing) {
      return;
    }
    this.isResizing = false;
    this.detachResizeListeners();
    if (this.hasMoved) {
      this.saveSize();
    }
  }

  private applySize(width: number, height: number): void {
    this.desiredWidth = width;
    this.desiredHeight = height;
    const maxWidth = Math.floor(window.innerWidth * this.maxWidthRatio);
    const maxHeight = Math.floor(window.innerHeight * this.maxHeightRatio);
    const clampedWidth = Math.min(Math.max(width, this.minWidth), maxWidth);
    const clampedHeight = Math.min(Math.max(height, this.minHeight), maxHeight);
    if (this.modalEl) {
      this.modalEl.style.setProperty('--width', `${clampedWidth}px`);
      this.modalEl.style.setProperty('--height', `${clampedHeight}px`);
      this.modalEl.style.setProperty('--min-width', `${this.minWidth}px`);
      this.modalEl.style.setProperty('--min-height', `${this.minHeight}px`);
    }
  }

  private getCurrentWidth(): number {
    const current = this.modalEl?.style.getPropertyValue('--width') || '';
    const parsed = parseInt(current || '0', 10);
    return Number.isNaN(parsed) ? Math.round(window.innerWidth * 0.9) : parsed;
  }

  private getCurrentHeight(): number {
    const current = this.modalEl?.style.getPropertyValue('--height') || '';
    const parsed = parseInt(current || '0', 10);
    return Number.isNaN(parsed) ? Math.round(window.innerHeight * 0.8) : parsed;
  }

  private saveSize(): void {
    const payload = {
      width: this.getCurrentWidth(),
      height: this.getCurrentHeight(),
    };
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch {
      // Ignore storage errors
    }
  }

  private loadSize(): { width: number; height: number } | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { width?: number; height?: number };
      if (!parsed.width || !parsed.height) {
        return null;
      }
      return { width: parsed.width, height: parsed.height };
    } catch {
      return null;
    }
  }

  private resolveModalEl(): void {
    if (this.modalEl) {
      return;
    }
    const host = this.hostRef.nativeElement;
    const modal = host.closest('ion-modal') as HTMLIonModalElement | null;
    if (modal) {
      this.modalEl = modal;
      if (this.desiredWidth && this.desiredHeight) {
        this.applySize(this.desiredWidth, this.desiredHeight);
      }
      return;
    }
    this.modalController.getTop().then((top) => {
      this.modalEl = top || undefined;
      if (this.modalEl && this.desiredWidth && this.desiredHeight) {
        this.applySize(this.desiredWidth, this.desiredHeight);
      }
    });
  }

  private getActiveMedia(): Medium | null {
    if (!this.media.length) {
      return null;
    }
    const swiperEl = this.swiperRef?.nativeElement as any;
    const activeIndex = swiperEl?.swiper?.activeIndex ?? 0;
    return this.media[Math.max(0, Math.min(activeIndex, this.media.length - 1))] || null;
  }

  private getMediaDownloadUrl(fileId: string): string | null {
    if (!fileId) {
      return null;
    }
    if (fileId.startsWith('blob:') || fileId.startsWith('data:')) {
      return fileId;
    }
    if (fileId.length === 36 && fileId.indexOf('/') < 0) {
      return `${environment.apiUrl}/medias/${fileId}/download`;
    }
    return fileId;
  }
}
